import { useState } from 'react';
import PropTypes from 'prop-types';

const STATUS_CLASS = {
	added: 'dsv-ucp-review__role--added',
	unchanged: 'dsv-ucp-review__role--unchanged',
	removed: 'dsv-ucp-review__role--removed',
};

/**
 * Sum added/removed role counts across the whole review model. Pure derivation
 * from the passed model — no extra props, no mutation.
 * @param {Array<object>} reviewModel
 * @returns {{ added: number, removed: number }}
 */
function countChanges(reviewModel) {
	let added = 0;
	let removed = 0;
	for (const group of reviewModel || []) {
		for (const persona of group.personas || []) {
			for (const role of persona.roles || []) {
				if (role.status === 'added') added += 1;
				else if (role.status === 'removed') removed += 1;
			}
		}
	}
	return { added, removed };
}

/** True when every role in the group is unchanged (nothing to add or remove). */
function isNoChangeGroup(group) {
	return (group.personas || []).every((persona) =>
		(persona.roles || []).every((role) => role.status === 'unchanged'),
	);
}

/**
 * Per-group tally of added/removed roles — drives the collapsed header hint.
 * Pure derivation from the group; no mutation.
 * @param {{ personas?: Array<{ roles?: Array<{ status: string }> }> }} group
 * @returns {{ added: number, removed: number }}
 */
function groupChangeCounts(group) {
	let added = 0;
	let removed = 0;
	for (const persona of group.personas || []) {
		for (const role of persona.roles || []) {
			if (role.status === 'added') added += 1;
			else if (role.status === 'removed') removed += 1;
		}
	}
	return { added, removed };
}

/**
 * Seed the expanded-group set: groups WITH changes open, all-unchanged groups
 * collapsed. Returns a fresh Set so the caller owns the state.
 * @param {Array<object>} reviewModel
 * @returns {Set<string>}
 */
function seedExpandedGroups(reviewModel) {
	const open = new Set();
	for (const group of reviewModel || []) {
		if (!isNoChangeGroup(group)) open.add(group.useCaseId);
	}
	return open;
}

/**
 * Whole-persona status derived from its roles: 'added' when every role is added,
 * 'removed' when every role is removed, otherwise '' (mixed or all unchanged →
 * neutral). Pure derivation — no props, no mutation.
 * @param {{ roles?: Array<{ status: string }> }} persona
 * @returns {'added' | 'removed' | ''}
 */
function personaStatus(persona) {
	const roles = persona.roles || [];
	if (roles.length === 0) return '';
	if (roles.every((role) => role.status === 'added')) return 'added';
	if (roles.every((role) => role.status === 'removed')) return 'removed';
	return '';
}

/**
 * Step-2 presentation: the complete resulting link set grouped by use case →
 * persona → roles. A summary bar tallies the pending add/remove counts; new
 * roles render as green "(new)" pills, removed roles as struck-through red
 * "(removed)" pills, unchanged roles as neutral pills. Presentational only.
 */
function ManageLinksReview({ reviewModel, isEmptyDiff }) {
	const { added, removed } = countChanges(reviewModel);

	// Which use-case groups are expanded. Seeded once on mount (changed groups
	// open, unchanged collapsed); the modal remounts this on each Review entry.
	const [expandedIds, setExpandedIds] = useState(() => seedExpandedGroups(reviewModel));

	const toggleGroup = (useCaseId) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(useCaseId)) next.delete(useCaseId);
			else next.add(useCaseId);
			return next;
		});
	};

	return (
		<div className="dsv-ucp-review">
			{isEmptyDiff && (
				<p className="dsv-ucp-review__empty" data-testid="ucp-review-empty">
					No changes to apply.
				</p>
			)}
			{!isEmptyDiff && (added > 0 || removed > 0) && (
				<div className="dsv-ucp-review__summary">
					{added > 0 && (
						<span
							className="dsv-ucp-review__chip dsv-ucp-review__chip--added"
							data-testid="ucp-review-added-count"
						>
							{added} to add
						</span>
					)}
					{removed > 0 && (
						<span
							className="dsv-ucp-review__chip dsv-ucp-review__chip--removed"
							data-testid="ucp-review-removed-count"
						>
							{removed} to remove
						</span>
					)}
				</div>
			)}
			{reviewModel.map((group) => {
				const noChange = isNoChangeGroup(group);
				const open = expandedIds.has(group.useCaseId);
				const { added: gAdded, removed: gRemoved } = groupChangeCounts(group);
				return (
					<div
						className={`dsv-ucp-review__group${noChange ? ' dsv-ucp-review__group--nochange' : ' dsv-ucp-review__group--changed'}`}
						key={group.useCaseId}
						data-testid={`ucp-review-uc-${group.useCaseId}`}
					>
						<button
							type="button"
							className="dsv-ucp-review__acc-header"
							aria-expanded={open}
							data-testid={`ucp-review-acc-header-${group.useCaseId}`}
							onClick={() => toggleGroup(group.useCaseId)}
						>
							<span className="dsv-ucp-review__acc-chevron" aria-hidden="true">
								{open ? '▾' : '▸'}
							</span>
							<span className="dsv-ucp-review__usecase">{group.useCaseName}</span>
							{noChange ? (
								<span className="dsv-ucp-review__group-hint">No changes</span>
							) : (
								<span className="dsv-ucp-review__acc-hint">
									{gAdded > 0 && (
										<span className="dsv-ucp-review__acc-hint--added">{`+${gAdded}`}</span>
									)}
									{gAdded > 0 && gRemoved > 0 && ' / '}
									{gRemoved > 0 && (
										<span className="dsv-ucp-review__acc-hint--removed">{`−${gRemoved}`}</span>
									)}
								</span>
							)}
						</button>
						{open && (
							<div className="dsv-ucp-review__acc-body">
								{group.personas.map((persona) => {
									const pStatus = personaStatus(persona);
									return (
									<div
										className={`dsv-ucp-review__persona${pStatus ? ` dsv-ucp-review__persona--${pStatus}` : ''}`}
										key={persona.personaId}
										data-testid={`ucp-review-persona-${group.useCaseId}-${persona.personaId}`}
									>
										<span className="dsv-ucp-review__persona-name">{persona.personaName}</span>
										<span className="dsv-ucp-review__roles">
											{persona.roles.map((role) => (
												<span
													key={role.roleValue}
													className={`dsv-ucp-review__role ${STATUS_CLASS[role.status] || ''}`}
												>
													<span className="dsv-ucp-review__role-label">{role.roleLabel}</span>
													{role.status === 'added' && ' (new)'}
													{role.status === 'removed' && ' (removed)'}
												</span>
											))}
										</span>
									</div>
									);
								})}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}

ManageLinksReview.propTypes = {
	reviewModel: PropTypes.arrayOf(PropTypes.object).isRequired,
	isEmptyDiff: PropTypes.bool,
};

export default ManageLinksReview;
